import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  type ElementTransformer,
} from '@lexical/markdown';
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from '@lexical/react/LexicalHorizontalRuleNode';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import { $createTextNode, $isParagraphNode } from 'lexical';

/**
 * Custom markdown transformers for nodes that `@lexical/markdown` does not cover
 * out of the box. Without these, inserting a horizontal rule or a table into a
 * markdown-backed editor would render fine but vanish on save, because the
 * serializer has no syntax to emit for them. Each transformer round-trips:
 * editor node -> markdown -> editor node with no loss.
 */

// Table cells hold inline content only (bold, italic, code, links). Using the
// full transformer set here would recurse into block/table parsing inside a
// cell, so we restrict cell (de)serialization to the inline transformers.
const CELL_TRANSFORMERS = [
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
];

// ---------------------------------------------------------------------------
// Horizontal rule  (`---`)
// ---------------------------------------------------------------------------

export const HR: ElementTransformer = {
  dependencies: [HorizontalRuleNode],
  export: (node) => ($isHorizontalRuleNode(node) ? '---' : null),
  // Whole line is exactly a thematic break, so `***bold***` never matches.
  regExp: /^(---|\*\*\*|___)\s?$/,
  replace: (parentNode, _children, _match, isImport) => {
    const line = $createHorizontalRuleNode();
    if (isImport || parentNode.getNextSibling() != null) {
      parentNode.replace(line);
    } else {
      parentNode.insertBefore(line);
    }
    line.selectNext();
  },
  type: 'element',
};

// ---------------------------------------------------------------------------
// GFM pipe tables
// ---------------------------------------------------------------------------

const TABLE_ROW_REG_EXP = /^\|(.+)\|\s*$/;
// A divider row: `| --- | :--: | ---: |`. Its presence is what turns the
// preceding pipe line into a table header.
const TABLE_ROW_DIVIDER_REG_EXP = /^\|(?:\s*:?-+:?\s*\|)+\s*$/;

// Cells are pipe-delimited, but a literal `|` inside a cell is escaped as `\|`.
// Split only on unescaped pipes so escaped ones stay within the cell.
function splitRowCells(inner: string): string[] {
  return inner.split(/(?<!\\)\|/);
}

function $createCell(rawCell: string): TableCellNode {
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
  // Unescape `\|` back to `|` before re-parsing the cell's inline markdown.
  const text = rawCell.replace(/\\\|/g, '|').trim();
  $convertFromMarkdownString(text, CELL_TRANSFORMERS, cell);
  return cell;
}

function $mapToTableCells(line: string): TableCellNode[] | null {
  const match = line.match(TABLE_ROW_REG_EXP);
  if (!match || !match[1]) return null;
  return splitRowCells(match[1]).map((cell) => $createCell(cell));
}

function $rowToCells(row: TableRowNode): string[] {
  const cells: string[] = [];
  for (const cell of row.getChildren()) {
    if (!$isTableCellNode(cell)) continue;
    // Collapse newlines (one cell = one line) and escape pipes so they don't
    // read as extra columns on re-import.
    const text = $convertToMarkdownString(CELL_TRANSFORMERS, cell)
      .replace(/\n/g, ' ')
      .trim()
      .replace(/\|/g, '\\|');
    cells.push(text);
  }
  return cells;
}

export const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node) => {
    if (!$isTableNode(node)) return null;
    const lines: string[] = [];
    node.getChildren().forEach((row, rowIndex) => {
      if (!$isTableRowNode(row)) return;
      const cells = $rowToCells(row);
      lines.push(`| ${cells.join(' | ')} |`);
      // GFM requires a divider after the header (first) row.
      if (rowIndex === 0) {
        lines.push(`| ${cells.map(() => '---').join(' | ')} |`);
      }
    });
    return lines.join('\n');
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _children, match) => {
    const line = match[0];
    // The importer strips the matched text off `parentNode` before calling us
    // (our regExp matches the whole line). Restore it so a pipe line we choose
    // not to convert survives as literal text instead of an empty paragraph.
    const keepAsText = () => {
      parentNode.clear();
      parentNode.append($createTextNode(line));
    };

    // A divider promotes the preceding pipe line (the header) into a table. This
    // gate is what stops a stray `| status |` paragraph from being silently
    // rewritten as a table — conversion only happens when a divider follows.
    if (TABLE_ROW_DIVIDER_REG_EXP.test(line)) {
      const header = parentNode.getPreviousSibling();
      if ($isParagraphNode(header)) {
        const cells = $mapToTableCells(header.getTextContent());
        if (cells) {
          const table = $createTableNode();
          const headerRow = $createTableRowNode();
          for (const cell of cells) {
            cell.setHeaderStyles(TableCellHeaderStates.ROW);
            headerRow.append(cell);
          }
          table.append(headerRow);
          header.replace(table);
          parentNode.remove();
          return;
        }
      }
      // No header above — leave the `| --- |` line as literal text.
      keepAsText();
      return;
    }

    // A data row only joins a table a divider already started; a lone pipe line
    // (e.g. the header before its divider arrives, or a stray `| status |`)
    // stays plain text so it round-trips unchanged.
    const previous = parentNode.getPreviousSibling();
    if (!$isTableNode(previous)) {
      keepAsText();
      return;
    }
    const cells = $mapToTableCells(line);
    if (cells == null) {
      keepAsText();
      return;
    }
    const row = $createTableRowNode();
    for (const cell of cells) row.append(cell);
    previous.append(row);
    parentNode.remove();
  },
  type: 'element',
};

/** Block-level transformers to prepend to the default `TRANSFORMERS`. */
export const BLOCK_TRANSFORMERS = [TABLE, HR];
